import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { mutateRequest, postFetcher } from "@/lib/swr";
import { refreshRcTasks } from "@/constants/rc";

// Reject an RC approval (assignees / admin), echoing the reason back to the
// requester. Two flavors: intake approval (closes the RC, terminal) and
// transition review (`review` = deploy/close — bounces the application back,
// the RC keeps its status and the requester may resubmit). Cannot be undone.
const RcRejectButton = ({
  taskId,
  rcId,
  review,
  onRejected,
}: {
  taskId: string;
  rcId: string;
  /** 流转申请审批:拒绝只打回申请,RC 不关闭 */
  review?: "deploy" | "close";
  /** 额外刷新(如 inbox 列表);RC 自身的列表总会刷新 */
  onRejected?: () => void;
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const { trigger, isMutating } = useSWRMutation(
    `/rc-tasks/${taskId}/reject`,
    postFetcher,
    {
      onSuccess: () => {
        setOpen(false);
        refreshRcTasks(rcId);
        mutateRequest(`/remote-configs/${rcId}`);
        mutateRequest("/remote-configs");
        mutateRequest("/remote-configs/stats");
        onRejected?.();
      },
      onError: (error: Error) =>
        toastError(toast, "Failed to reject approval", error),
    }
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) setReason("");
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => e.stopPropagation()}
          className="h-7 gap-1 px-1.5 text-xs text-destructive hover:text-destructive"
        >
          <XIcon className="h-3.5 w-3.5" />
          Reject
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {review === "deploy"
              ? "拒绝部署申请"
              : review === "close"
              ? "拒绝回滚申请"
              : "拒绝 RC 审批"}
          </DialogTitle>
          <DialogDescription>
            {review === "deploy"
              ? "拒绝后打回需求方,RC 留在实验中;需求方修订数据 report 后可重新提交部署申请。不可撤销。"
              : review === "close"
              ? "拒绝后打回需求方,RC 状态不变;如需下线可重新发起回滚。不可撤销。"
              : "拒绝后该 RC 直接关闭并通知需求方,不可撤销;如需继续需重新提交申请。"}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          rows={3}
          placeholder="拒绝理由(可选,会随通知发给需求方)"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setReason(e.target.value)
          }
        />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={isMutating}
            onClick={() => trigger({ reason: reason.trim() || undefined })}
          >
            {isMutating ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RcRejectButton;
